// component.jsx — CardAttachmentsComponent (UI principal, simétrico a otros cards)
import { useMemo } from "preact/hooks";
import { useCardAttachments } from "./store";
import {
  Box, Typography, Chip, Divider, List, ListItem, ListItemText,
  ListItemIcon, IconButton, Tooltip, Stack, Card, CardContent, Avatar, CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";

const StyledCardAttachments = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const fileType = (name) => {
  const ext = String(name || "").toLowerCase().split(".").pop();
  if (["jpg","jpeg","png","gif","webp"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "document";
};
const fileIcon = (name) => {
  const t = fileType(name);
  if (t === "image") return <ImageIcon />;
  if (t === "pdf") return <PictureAsPdfIcon />;
  return <DescriptionIcon />;
};

export const CardAttachmentsComponent = () => {
  const {
    quotation, role,
    attachments, count, canAddMore, MAX_ATTACHMENTS,
    selectedId, setSelectedId,
    openAddModal, openDeleteModal,
    uploading, deleting,
  } = useCardAttachments();

  if (!quotation) return null;

  const remaining = useMemo(() => Math.max(0, MAX_ATTACHMENTS - count), [MAX_ATTACHMENTS, count]);
  const deleteDisabled = !selectedId;

  const handleDownload = (att) => {
    try {
      const baseUrl = import.meta.env.DEV ? "http://localhost:3000" : window.location.origin;
      const qid = quotation?._id || quotation?.id || quotation?.idQuotation;
      const endpoint = `/${role}/api/quotations/${qid}/download/${encodeURIComponent(att.name)}`;
      const sessionId = localStorage.getItem("sessionId");
      const form = document.createElement("form");
      form.method = "GET";
      form.action = baseUrl + endpoint;
      form.style.display = "none";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "sessionId";
      input.value = sessionId || "";
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (e) { /* noop */ }
  };
  const theme = useTheme();
  return (
    <StyledCardAttachments elevation={1}>
      {/* Header primary */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 1.5,
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" color="primary.contrastText" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <AttachFileIcon fontSize="small" color="primary.contrastText"/> Archivos Adjuntos ({count}/{MAX_ATTACHMENTS})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={canAddMore ? `Añadir archivos (máx ${remaining} restantes)` : "Límite de adjuntos alcanzado"}>
            <span>
              <IconButton style={{ color: theme.palette.primary.contrastText }} onClick={() => openAddModal()} disabled={!canAddMore || uploading}>
                {uploading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : <AddCircleOutlineIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={deleteDisabled ? "Selecciona un archivo para eliminar" : "Eliminar archivo seleccionado"}>
            <span>
              <IconButton style={{ color: theme.palette.error.light }} onClick={() => openDeleteModal(selectedId)} disabled={deleteDisabled || deleting}>
                {deleting ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : <DeleteOutlineIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <CardContent>
        <Divider sx={{ my: 1 }} />

        {!attachments?.length ? (
          <Typography variant="body2" color="text.secondary">
            No hay archivos adjuntos para esta cotización.
          </Typography>
        ) : (
          <List dense disablePadding>
            {attachments.map((att) => {
              const id = att?._id || att?.id || att?.name;
              const isSelected = id === selectedId;
              return (
                <ListItem
                  key={id}
                  onClick={() => setSelectedId(id)}
                  sx={{
                    borderRadius: 1, px: 1, cursor: "pointer",
                    bgcolor: isSelected ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                    alignItems: "flex-start",
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                      title="Descargar"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ mt: 0.3 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>{fileIcon(att?.name)}</Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: "caption" }}
                    primary={att?.name || "(Sin nombre)"}
                    secondary={att?.size ? `${(att.size/1024/1024).toFixed(2)} MB` : (att?.type || "document")}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </StyledCardAttachments>
  );
};
